/**
 * Οθόνη «η εφαρμογή μεταφέρθηκε».
 *
 * Εμφανίζεται όταν ο server απαντήσει 410 store_migrated (βλ. utils/migration.ts).
 * Καλύπτει ΤΑ ΠΑΝΤΑ και ΔΕΝ κλείνει: ο σκοπός είναι ακριβώς να μην μπορεί κανείς
 * να συνεχίσει να δουλεύει στον παλιό server, όπου οι παραγγελίες θα γράφονταν
 * σε βάση που δεν κοιτά κανείς.
 *
 * Έχει δική της σταθερή ταυτότητα και ΔΕΝ ακολουθεί το θέμα: είναι μήνυμα
 * συστήματος πάνω από οτιδήποτε — και από τη σελίδα σύνδεσης.
 */

import React, { useEffect, useState } from 'react';
import { ArrowRight, Headset, MoveRight } from 'lucide-react';
import flowLogo from '../assets/logos/logo.png';
import { SUPPORT_PHONE, SUPPORT_PHONE_DISPLAY } from './SupportContact';
import { MIGRATION_EVENT, getMovedTo } from '../utils/migration';

const displayHost = (url: string) => {
  try { return new URL(url).host; } catch { return url; }
};

const MigrationScreen: React.FC = () => {
  // Ελέγχουμε ΚΑΙ κατά την εκκίνηση: το πρώτο αίτημα (έλεγχος συνεδρίας) μπορεί
  // να έχει ήδη απαντήσει 410 πριν τοποθετηθεί αυτό το component.
  const [newUrl, setNewUrl] = useState<string | null>(getMovedTo());

  useEffect(() => {
    const onMoved = (e: Event) => setNewUrl((e as CustomEvent).detail?.newUrl || getMovedTo());
    window.addEventListener(MIGRATION_EVENT, onMoved);
    return () => window.removeEventListener(MIGRATION_EVENT, onMoved);
  }, []);

  if (!newUrl) return null;
  const host = displayHost(newUrl);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="migration-title"
      className="fixed inset-0 z-[2147483647] flex items-center justify-center p-6 overflow-y-auto"
      style={{ background: 'radial-gradient(ellipse at 30% 20%, #1e3a8a 0%, #0f172a 55%, #020617 100%)' }}
    >
      <div className="w-full max-w-lg text-center">
        <img src={flowLogo} alt="Xentrix Flow" className="w-16 h-16 mx-auto mb-8 drop-shadow-2xl" />

        <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-200 text-xs font-semibold tracking-wide uppercase">
          <MoveRight className="w-3.5 h-3.5" />
          Νέα διεύθυνση
        </div>

        <h1 id="migration-title" className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
          Η εφαρμογή μεταφέρθηκε
        </h1>
        <p className="text-base sm:text-lg text-slate-300 mb-8 leading-relaxed">
          Το κατάστημά σας εξυπηρετείται πλέον από νέα διεύθυνση.
          Εδώ δεν μπορείτε να συνεχίσετε — όλα τα δεδομένα σας βρίσκονται ήδη εκεί.
        </p>

        <div className="mb-8 px-5 py-4 rounded-2xl bg-white/5 border border-white/10">
          <div className="text-xs text-slate-400 mb-1.5">Η νέα διεύθυνση</div>
          <div className="text-xl sm:text-2xl font-bold text-white font-mono break-all">{host}</div>
        </div>

        <button
          type="button"
          onClick={() => { window.location.href = newUrl; }}
          className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-2xl
                     bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-400 hover:to-violet-500
                     text-white text-lg font-bold shadow-2xl shadow-blue-900/50
                     transition-all active:scale-[0.98]"
          autoFocus
        >
          Μετάβαση στη νέα διεύθυνση
          <ArrowRight className="w-5 h-5" />
        </button>

        <p className="mt-5 text-sm text-slate-400">
          Αποθηκεύστε τη νέα διεύθυνση στα αγαπημένα σας για την επόμενη φορά.
        </p>

        <a
          href={`tel:${SUPPORT_PHONE}`}
          className="mt-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <Headset className="w-4 h-4" />
          Χρειάζεστε βοήθεια; {SUPPORT_PHONE_DISPLAY}
        </a>
      </div>
    </div>
  );
};

export default MigrationScreen;
